import React, { useRef } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const getOctets = (value) => {
  if (!Array.isArray(value) || value.length !== 4) return ["", "", "", ""];
  return value.map((item) => item ?? "");
};

const SegmentedIpv4Input = ({
  value,
  displayValue,
  width = 300,
  disabled = false,
  borderless = false,
  error = false,
  helperText = "",
  sx,
  onChange,
}) => {
  const theme = useTheme();
  const inputRefs = useRef([]);
  const octets = getOctets(displayValue ?? value);

  const updateOctet = (index, nextValue) => {
    if (!/^\d{0,3}$/.test(nextValue)) return;

    const nextOctets = getOctets(value);
    nextOctets[index] = nextValue;
    onChange(nextOctets);

    if (nextValue.length === 3 && index < 3) {
      inputRefs.current[index + 1]?.focus();
      inputRefs.current[index + 1]?.select();
    }
  };

  return (
    <Box sx={{ width }}>
      <Box
        sx={{
          width: "100%",
          height: 31,
          display: "grid",
          gridTemplateColumns: "1fr 10px 1fr 10px 1fr 10px 1fr",
          alignItems: "center",
          border: borderless
            ? "none"
            : `1px solid ${error
              ? theme.palette.primary.redPrimary
              : theme.palette.primary.greyFifth
            }`,
          borderRadius: borderless ? 0 : theme.shape.customBorderRadius[1],
          backgroundColor: disabled
            ? theme.palette.primary.greyThread
            : theme.palette.primary.white,
          px: 1,
          opacity: disabled ? 0.7 : 1,
          boxSizing: "border-box",
          ...sx,
        }}
      >
        {octets.map((octet, index) => (
          <React.Fragment key={index}>
            <TextField
              inputRef={(element) => {
                inputRefs.current[index] = element;
              }}
              value={octet}
              disabled={disabled}
              onChange={(event) => updateOctet(index, event.target.value)}
              variant="standard"
              inputProps={{
                maxLength: 3,
                style: { textAlign: "center" },
              }}
              sx={{
                "& .MuiInputBase-input": {
                  p: 0,
                  textAlign: "center",
                  fontSize: theme.typography.customFontSize[2],
                },
                "& .MuiInput-underline:before, & .MuiInput-underline:after": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline.Mui-disabled:before": {
                  borderBottom: "none",
                },
              }}
            />
            {index < 3 && (
              <Typography sx={{ textAlign: "center", color: theme.palette.primary.greyPrimary }}>
                .
              </Typography>
            )}
          </React.Fragment>
        ))}
      </Box>
      {helperText && (
        <Typography
          sx={{
            color: theme.palette.primary.redPrimary,
            fontSize: theme.typography.customFontSize[1],
            lineHeight: "18px",
            mt: "2px",
            mb: "8px",
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default SegmentedIpv4Input;
